import { DEFAULT_PAGE_SIZE } from "constant/app";
import { DEVICE_CONNECTED_OPTIONS } from "constant/device-connected";
import { DEVICE_STATUS_SELECT_OPTIONS } from "constant/device-status";
import { PropSelectOptions } from "constant/prop-types";
import {
  formatAttributeList,
  formatNetworkLink,
  formatPrimaryLink,
  formatStrategyLink,
  formatTags,
} from "fleet/view/device/table-format";
import PropTypes from "prop-types";
import React from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { ClearableInput } from "ui/clearable-input";
import { ConnectedDeviceStats } from "ui/device-stats/connected-device-stats";
import { NetworkStatsBox } from "ui/device-stats/network-stats-box";
import { Provider } from "ui/fetch-options";
import {
  Filter,
  FILTER_TYPE_MULTI_SELECT,
  FILTER_TYPE_SELECT,
  filterOptionMapper,
  getFilterOptions,
  SENDER_USER,
} from "ui/filter";
import { NavLoader } from "ui/loader";
import { Paginator, Sorter, Searcher } from "ui/query-string";
import { Table, Column } from "ui/table";
import { DeviceStatusIcon } from "ui/table/format/device-status-icon";
import * as QS from "util/query-string";
import { genCancelSourceOptions } from "util/request";
import { sortByLabelAsc } from "util/sort-by-prop";
import { Box } from "view/layout/box";

const STATUS_SELECT_OPTIONS = getFilterOptions(
  DEVICE_STATUS_SELECT_OPTIONS,
).sort(sortByLabelAsc);

class FullDeviceListTable extends React.PureComponent {
  static get propTypes() {
    return {
      currentNetwork: PropTypes.object,
      // Lite devices
      devices: PropTypes.object.isRequired,
      deviceStatusOnline: PropTypes.bool,
      fetchDevices: PropTypes.func.isRequired,
      // Full devices + device status.
      fullDevices: PropTypes.array.isRequired,
      // Full Devices api state - incl. pagination metadata.
      fullDevicesState: PropTypes.object.isRequired,
      hideSidebar: PropTypes.func.isRequired,
      history: PropTypes.object.isRequired,
      location: PropTypes.object.isRequired,
      networkFilter: PropTypes.bool,
      playerVersionsOptions: PropSelectOptions.isRequired,
      // Enable / disable full-text query search.
      search: PropTypes.bool,
      showSidebar: PropTypes.func.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      tagsOptions: PropSelectOptions.isRequired,
      unloadDevices: PropTypes.func.isRequired,
      venueSyncEnabled: PropTypes.bool.isRequired,
    };
  }

  static get defaultProps() {
    return {
      networkFilter: true,
      search: false,
    };
  }

  constructor(props) {
    super(props);

    const { history } = props;

    this._paginator = new Paginator(history);
    this._searcher = new Searcher(history);
    this._sorter = new Sorter(history, ["id"]);

    this._beginFetch = this._beginFetch.bind(this);
    this._formatAttributes = this._formatAttributes.bind(this);
    this._formatStatus = this._formatStatus.bind(this);
    this._onFilterChange = this._onFilterChange.bind(this);
    this._onPageChange = this._onPageChange.bind(this);
    this._onRowSelect = this._onRowSelect.bind(this);
    this._onSearch = this._onSearch.bind(this);
    this._onSortChange = this._onSortChange.bind(this);
    this.getFilterApi = this.getFilterApi.bind(this);

    this._isMounted = false;
    this.cancelSource = null;
    this.filterApi = null;
    this.options = {
      onPageChange: this._onPageChange,
      sizePerPageList: [DEFAULT_PAGE_SIZE, 25, 50],
      onSortChange: this._onSortChange,
    };
    this.selectRow = {
      bgColor: "#d3e6f1",
      clickToSelect: true,
      hideSelectColumn: true,
      mode: "radio",
      onSelect: this._onRowSelect,
    };

    this.state = {
      inFlight: false,
      initialQuery: this._searcher.getSearchQuery(),
      ...this.getState(props),
    };
  }

  componentDidUpdate(prevProps) {
    const deviceChanged = this.props.devices !== prevProps.devices;
    if (deviceChanged) {
      this.setState(this.getState(this.props));
    }
  }

  componentDidMount() {
    this._isMounted = true;
    this._paginator.initPaginationQueryParams();
    this._beginFetch();
  }

  componentWillUnmount() {
    this._isMounted = false;
    // Clear UI state
    const { unloadDevices } = this.props;

    if (this.cancelSource) {
      this.cancelSource.cancel();
    }

    unloadDevices();
  }

  _getLocationParams(location) {
    const { search } = location;
    return QS.parse(search);
  }

  getState(props) {
    const networks = Object.values(props.devices).reduce((acc, device) => {
      acc[device.network_id] = device.network_name;

      return acc;
    }, {});

    return {
      networkOptions: this.getOptions(networks),
    };
  }

  getOptions(idValObject) {
    return Object.entries(idValObject)
      .map(([id, value]) => {
        return {
          value: id,
          label: value,
        };
      })
      .map(filterOptionMapper)
      .sort(sortByLabelAsc);
  }

  getFilterApi(filterApi) {
    this.filterApi = filterApi;
  }

  _getFilterFields() {
    const { networkFilter, playerVersionsOptions, tagsOptions } = this.props;
    const { networkOptions } = this.state;

    return [
      {
        label: "Status",
        name: "status",
        placeholder: "Device Status",
        helpText:
          "Filter results for device status delayed by up to 5 minutes.",
        showPlaceholder: true,
        type: FILTER_TYPE_SELECT,
        options: STATUS_SELECT_OPTIONS,
      },
      ...(networkFilter
        ? [
            {
              label: "Network",
              name: "network_id",
              placeholder: "Network",
              showPlaceholder: true,
              type: FILTER_TYPE_SELECT,
              options: networkOptions,
            },
          ]
        : []),
      {
        label: "Tags",
        name: "tags",
        placeholder: "Tags",
        showPlaceholder: true,
        type: FILTER_TYPE_MULTI_SELECT,
        options: tagsOptions,
      },
      {
        label: "Player Version",
        name: "current_version",
        placeholder: "Version",
        showPlaceholder: true,
        type: FILTER_TYPE_SELECT,
        options: playerVersionsOptions,
      },
      {
        label: "Device Has Connected",
        name: "has_connected",
        placeholder: "Device Has Connected",
        showPlaceholder: true,
        type: FILTER_TYPE_SELECT,
        options: DEVICE_CONNECTED_OPTIONS,
      },
    ];
  }

  _getDeviceMap() {
    const { devices, fullDevicesState } = this.props;
    const { all_ids: deviceIds } = fullDevicesState;

    return deviceIds.reduce((deviceMap, pk) => {
      deviceMap[pk] = devices[pk];

      return deviceMap;
    }, {});
  }

  _onFilterChange(filterState) {
    const { filterApi } = this;
    const { history, location } = this.props;
    const { pathname } = location;

    if (!filterApi) {
      console.error("filterApi not initialized!");
      return;
    }

    if (filterState.sender !== SENDER_USER) {
      return;
    }

    history.push({ pathname, search: filterApi.serializeFilters(filterState) });

    return this._beginFetch();
  }

  _onRowSelect(row, isSelected, _event) {
    const { hideSidebar, showSidebar } = this.props;

    if (isSelected) {
      showSidebar(row);
    } else {
      hideSidebar();
    }
  }

  _formatStatus(_status, device) {
    return (
      <DeviceStatusIcon
        device={device}
        deviceStatusOnline={this.props.deviceStatusOnline}
      />
    );
  }

  _beginFetch(opts) {
    const doFetch = this._fetchDevices.bind(this, opts);

    this.setState({ inFlight: true }, doFetch);
  }

  _formatAttributes(systemManaged, device) {
    const { venueSyncEnabled } = this.props;

    return formatAttributeList(
      device.os_platform,
      systemManaged,
      venueSyncEnabled,
    );
  }

  _fetchDevices(opts = {}) {
    const { fetchDevices, location } = this.props;
    const params = this._getLocationParams(location);

    const options = {
      ...opts,
      ...genCancelSourceOptions(),
    };
    this.cancelSource = options.cancelSource;

    return fetchDevices(params, options).finally(() => {
      if (this._isMounted) {
        this.setState({ inFlight: false });
      }
    });
  }

  _onSearch(query) {
    this._searcher.onSearchChange(query);

    return this._beginFetch();
  }

  _onPageChange(page, sizePerPage) {
    this._paginator.onPageChange(page, sizePerPage);

    return this._beginFetch();
  }

  _onSortChange(sortName, sortOrder) {
    this._sorter.onSortChange(sortName, sortOrder);

    return this._beginFetch();
  }

  _renderStatsBox() {
    const { currentNetwork } = this.props;
    const deviceMap = this._getDeviceMap();

    return (
      <ConnectedDeviceStats devices={deviceMap}>
        {currentNetwork && (
          <Col>
            <NetworkStatsBox network={currentNetwork} />
          </Col>
        )}
      </ConnectedDeviceStats>
    );
  }

  _renderFilter() {
    const filterFields = this._getFilterFields();
    const { location } = this.props;

    return (
      <Filter
        onFilterChange={this._onFilterChange}
        fields={filterFields}
        getApi={this.getFilterApi}
        invertible={true}
        location={location}
      />
    );
  }

  _renderSearch() {
    const { search } = this.props;

    if (!search) {
      return null;
    }

    const { initialQuery } = this.state;
    const submitButton = (
      <Button variant="secondary">
        <i className="fa fa-search" />
      </Button>
    );

    return (
      <Row>
        <Col xs={12}>
          <ClearableInput
            initialValue={initialQuery}
            onSubmit={this._onSearch}
            placeholder="Search"
            submitButton={submitButton}
            submitOnChange
          />
        </Col>
      </Row>
    );
  }

  _renderView() {
    const { fullDevicesState, fullDevices } = this.props;
    const { inFlight } = this.state;
    const { total } = fullDevicesState;

    const options = {
      ...this.options,
      ...this._paginator.getTablePageOptions(),
      fetchInfo: {
        dataTotalSize: total,
      },
    };

    const tableClass =
      "request-component" + (inFlight ? " request-in-flight" : "");

    return (
      <div>
        {inFlight && <NavLoader.Proxy>Fetching devices...</NavLoader.Proxy>}
        <div className={tableClass}>
          {this._renderStatsBox()}
          <Row>
            <Col xs={12} sm={8}>
              {this._renderFilter()}
            </Col>
            <Col xs={12} sm={4}>
              {this._renderSearch()}
            </Col>
          </Row>
          <Box title="" noHeader={true}>
            <Table
              data={fullDevices}
              hover
              onTableChange={(
                type,
                { page, sizePerPage, sortField, sortOrder, ...rest },
              ) => {
                if (type === "pagination") {
                  this._onPageChange(page, sizePerPage);
                } else if (type === "sort") {
                  this._onSortChange(sortField, sortOrder);
                }
              }}
              options={options}
              pagination
              remote
              rowClasses="clickable"
              selectRow={this.selectRow}
            >
              <Column
                key="status"
                dataField="status"
                width="70px"
                dataAlign="center"
                dataFormat={this._formatStatus}
                dataSort
              >
                Status
              </Column>
              <Column
                key="device_id"
                dataField="device_id"
                tdAttr={{ "data-device-id": "" }}
                isKey
                hidden
              >
                ID
              </Column>
              <Column
                key="name"
                dataField="name"
                width="15%"
                dataFormat={formatPrimaryLink}
                dataSort
                formatExtraData="device_id"
              >
                Name
              </Column>
              <Column
                key="strategy"
                dataField="strategy_name"
                dataFormat={formatStrategyLink}
                width="15%"
                dataSort
              >
                Strategy
              </Column>
              <Column
                key="network"
                dataField="network_name"
                dataFormat={formatNetworkLink}
                width="15%"
                dataSort
              >
                Network
              </Column>
              <Column
                key="version"
                dataField="current_version"
                width="90px"
                dataSort
              >
                Version
              </Column>
              <Column
                key="tag"
                dataField="tags"
                dataFormat={formatTags}
                width="15%"
              >
                Tags
              </Column>
              <Column
                key="system_managed"
                dataField="system_managed"
                dataFormat={this._formatAttributes}
                width="15%"
              >
                Attributes
              </Column>
            </Table>
            <span className="text-muted">
              <br />
              Device list results are delayed by up to 5 minutes.
            </span>
          </Box>
        </div>
      </div>
    );
  }

  renderLoaderAndView() {
    const { storesLoaded } = this.props;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    return this._renderView();
  }

  render() {
    const view = this.renderLoaderAndView();

    return (
      <div>
        <Provider.TagOptions />
        <Provider.PlayerVersionOptions />
        {view}
      </div>
    );
  }
}

export { FullDeviceListTable };
